import React, { useState, useEffect } from 'react';
import {
  Smartphone,
  Watch, ArrowRight, ShieldAlert,
  Power, CheckCircle
} from 'lucide-react';
import useDragToScroll from '../../hooks/useDragToScroll';

const TurnBasedDuelModal = ({
  isOpen,
  duelPhase, // 'victim_action' | 'protector_strike' | 'victim_failed'
  attackerProfile,
  userProfile,
  currentAgility = 80,
  currentCharisma = 90,
  onVictimAction,
  onDrawPhone,
  currentPhoneLocation = 'in_hand',
  activeProtector,
  attackerHp,
  maxAttackerHp,
  onContinueSimulation,
  onFinishIncident,
  parentTiles,
  actionHistory = [],
  arrivedProtectorsCount = 0,
  communityTelemetry = null,
  remainingSec = 25,
  onDoNothing,
  unlockedFeatures = ['placement_in_bag', 'guardians', 'shortcutHardware'],
  trustedContactsCount = 0,
}) => {
  const [feedback, setFeedback] = useState(null); // { success: boolean, message: string, detail: string }
  const [phoneLoc, setPhoneLoc] = useState(currentPhoneLocation);
  const [drawPhoneFeedback, setDrawPhoneFeedback] = useState(null);

  const modalContainerRef = useDragToScroll({ direction: 'vertical', speed: 1.3 });
  const modalCardRef = useDragToScroll({ direction: 'vertical', speed: 1.3 });

  useEffect(() => {
    setPhoneLoc(currentPhoneLocation);
    setDrawPhoneFeedback(null);
    setFeedback(null); // Réinitialisation impérative du feedback à chaque ouverture de modale !
  }, [currentPhoneLocation, isOpen]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        if (modalContainerRef.current) {
          modalContainerRef.current.scrollTop = 0;
        }
        if (modalCardRef.current) {
          modalCardRef.current.scrollTop = 0;
          modalCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, duelPhase, feedback, drawPhoneFeedback]);

  if (!isOpen) return null;

  const handleDoNothingClick = () => {
    if (onDoNothing) {
      onDoNothing();
    } else if (onContinueSimulation) {
      onContinueSimulation();
    }
  };

  const handleDrawPhoneClick = () => {
    // Sortir le téléphone de la poche ou du sac face à l'agresseur échoue et l'agresseur prend le téléphone
    setPhoneLoc('confiscated');
    if (onDrawPhone) {
      onDrawPhone('confiscated');
    }
    setFeedback({
      success: false,
      message: "❌ SMARTPHONE CONFISQUÉ : L'agresseur vous a arraché le téléphone !",
      detail: `Vous avez tenté de sortir votre smartphone de votre ${phoneLoc === 'in_bag' ? 'sac' : 'poche'}. L'agresseur a vu votre geste et vous a pris le téléphone avant que vous n'ayez pu envoyer l'alerte !`,
    });
    onVictimAction({ actionId: 'draw_phone', success: false });
  };

  // Calcul des actions STRICTEMENT POSSIBLES selon l'emplacement, les tuiles activées ET les déblocages XP
  const availableActions = [];

  // 1. Bouton SOS dans l'application : Uniquement si téléphone dans la main
  if (phoneLoc === 'in_hand') {
    availableActions.push({
      id: 'app_sos',
      label: "📱 Déclencher le SOS depuis l'Application",
      desc: "Appui direct sur le bouton SOS rouge à l'écran (Instantané & Gratuit)",
      isLocked: false,
      type: 'app_sos',
      icon: Smartphone,
      color: '#ef4444',
    });
  }

  // 3. Montre Connectée : Disponible dans TOUTES les positions (même si smartphone confisqué) si débloquée
  if (unlockedFeatures.includes('smartwatch')) {
    availableActions.push({
      id: 'smartwatch',
      label: "⌚ Déclencher le SOS depuis la Montre Connectée",
      desc: "Pression discrète sur le bouton SOS de la montre au poignet (Instantané & Inarrêtable)",
      isLocked: false,
      type: 'smartwatch',
      icon: Watch,
      color: '#ec4899',
    });
  }

  // 4. Touche Raccourcis (Volume ou Power fusionnés) : Disponible si téléphone pas confisqué et débloquée (60 XP)
  if (phoneLoc !== 'confiscated' && unlockedFeatures.includes('shortcutHardware')) {
    availableActions.push({
      id: 'shortcutHardware',
      label: phoneLoc === 'in_hand'
        ? "🔘 Presser la Touche Raccourcis (Volume/Power)"
        : phoneLoc === 'in_pocket'
        ? "🔘 Presser la Touche Raccourcis dans la poche"
        : "🔘 Presser la Touche Raccourcis à travers le sac",
      desc: "Raccourci matériel discret (Volume ou Power) (Instantané & Inarrêtable)",
      isLocked: false,
      type: 'shortcutHardware',
      icon: Power,
      color: '#38bdf8',
    });
  }

  const handleExecuteAction = (action) => {
    if (action.isLocked) return;

    // Seule l'Application SOS peut être contrée par l'agresseur (25% des cas), et uniquement une fois
    // la Touche Raccourcis débloquée (avant cela, la victime n'a que le bouton SOS de l'app : on ne
    // la prive pas de son unique option). Les raccourcis matériels et la montre sont inarrêtables.
    const hasShortcutHardware = unlockedFeatures.includes('shortcutHardware');
    const isImmune = Boolean(hasShortcutHardware && attackerProfile?.immunity === 'app_sos' && action.type === 'app_sos');

    if (isImmune) {
      setPhoneLoc('confiscated');
      if (onDrawPhone) {
        onDrawPhone('confiscated');
      }
      setFeedback({
        success: false,
        message: `❌ SMARTPHONE CONFISQUÉ : L'agresseur vous a arraché le téléphone !`,
        detail: `L'agresseur surveillait vos mains, a bloqué l'écran et vous a pris le téléphone avant que vous n'ayez pu envoyer l'alerte !`,
      });
      onVictimAction({ actionId: action.id, success: false });
    } else {
      const isCommunityActive = Boolean(unlockedFeatures.includes('guardians'));
      const contactsCount = (trustedContactsCount !== undefined && trustedContactsCount !== null)
        ? trustedContactsCount
        : (userProfile?.trustedContactsCount || 0);
      const contactsSeen = contactsCount > 0
        ? Math.min(contactsCount, Math.max(1, Math.floor(contactsCount * 0.7) + (Math.random() > 0.5 ? 1 : 0)))
        : 0;

      const realArea = communityTelemetry?.guardiansArea ?? 0;
      const realReceived = communityTelemetry?.guardiansReceived ?? 0;
      const realAccepted = communityTelemetry?.guardiansAccepted ?? 0;
      const realWhiteZone = communityTelemetry?.guardiansWhiteZone ?? 0;

      let detailText = '';
      if (contactsCount === 0 && !isCommunityActive) {
        detailText = "⚠️ Alerte transmise mais AUCUN SMS envoyé (0 contact de confiance configuré) et aucun protecteur alerté.";
      } else if (contactsCount === 0) {
        detailText = "L'alerte a été transmise aux Protecteurs MonAlerte (0 SMS envoyé : aucun contact de confiance configuré).";
      } else if (isCommunityActive) {
        detailText = `L'alerte a été transmise aux Protecteurs et à vos ${contactsCount} contact(s) d'urgence (${contactsSeen} ouverts).`;
      } else {
        detailText = `Alerte transmise à vos ${contactsCount} contact(s) de confiance (${contactsSeen} ouverts).`;
      }

      setFeedback({
        success: true,
        message: `⚡ ALERTE DÉCLENCHÉE AVEC SUCCÈS !`,
        detail: detailText,
        contactsCount,
        contactsSeen,
        isCommunityActive,
        guardiansArea: realArea,
        guardiansReceived: realReceived,
        guardiansAccepted: realAccepted,
        guardiansWhiteZone: realWhiteZone,
      });
      onVictimAction({ actionId: action.id, success: true });
    }
  };

  // Détection des limitations selon l'emplacement du téléphone
  const isPhoneInBagOrPocket = phoneLoc === 'in_bag' || phoneLoc === 'in_pocket';

  return (
    <div
      ref={modalContainerRef}
      className="duel-modal-overlay"
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(3, 7, 18, 0.85)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        borderRadius: '16px',
        padding: '8px',
        overflowY: 'auto',
        boxSizing: 'border-box',
        animation: 'fadeIn 0.2s ease',
      }}
    >
      <div
        ref={modalCardRef}
        style={{
          background: 'linear-gradient(175deg, rgba(13, 27, 46, 0.98) 0%, rgba(6, 12, 22, 0.99) 100%)',
          border: '2px solid ' + (duelPhase === 'protector_strike' ? '#10b981' : '#ef4444'),
          borderRadius: '14px',
          maxWidth: '940px',
          width: '100%',
          maxHeight: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        padding: '12px 14px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.95), 0 0 30px ' + (duelPhase === 'protector_strike' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.25)'),
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        animation: 'slideUpDuel 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards',
      }}>
        <style>{`
          @keyframes slideUpDuel {
            0% {
              opacity: 0;
              transform: translateY(20px) scale(0.98);
            }
            100% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          @media (max-width: 860px) {
            .duel-modal-overlay {
              align-items: flex-start !important;
              padding-top: 6px !important;
            }
          }
          .duel-responsive-grid {
            display: grid;
            grid-template-columns: 260px 1fr 260px;
            gap: 12px;
            align-items: start;
          }
          @media (max-width: 860px) {
            .duel-responsive-grid {
              display: flex !important;
              flex-direction: column !important;
              gap: 10px !important;
            }
            .duel-attacker-col {
              order: 1 !important;
            }
            .duel-user-col {
              order: 2 !important;
            }
            .duel-actions-col {
              order: 3 !important;
            }
          }
        `}</style>

        {/* ==================== CAS 1 : DUEL SARAH VS ATTAQUE AGRESSEUR ==================== */}
        {duelPhase === 'victim_action' && (
          <>
            {/* MESSAGE DE FEEDBACK DU TOUR & ÉCRAN DE DÉTAIL D'ENVOI */}
            {feedback && (
              <div style={{
                background: 'rgba(5, 11, 20, 0.9)',
                border: '2px solid ' + (feedback.success ? 'rgba(56, 189, 248, 0.6)' : '#ef4444'),
                borderRadius: '16px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
                animation: 'fadeIn 0.25s ease',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.85)',
              }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: feedback.success ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)',
                      border: '1.5px solid ' + (feedback.success ? '#10b981' : '#ef4444'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {feedback.success ? <CheckCircle size={24} color="#10b981" /> : <ShieldAlert size={24} color="#ef4444" />}
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '1.05rem', color: feedback.success ? '#4ade80' : '#f87171', fontWeight: 900 }}>
                        {feedback.message}
                      </h4>
                      <span style={{ fontSize: '0.80rem', color: '#cbd5e1', lineHeight: 1.4 }}>
                        {feedback.detail}
                      </span>
                    </div>
                  </div>

                  {/* TÉLÉMÉTRIE D'ENVOI DÉTAILLÉE DU SOS (EXPLICITE & PÉDAGOGIQUE) */}
                  {feedback.success && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                      gap: '12px',
                    }}>
                      {/* Bloc 1 : Envois SMS aux Contacts */}
                      <div style={{
                        background: 'rgba(5, 11, 20, 0.8)',
                        border: '1.5px solid ' + (feedback.contactsCount > 0 ? 'rgba(242, 184, 7, 0.4)' : 'rgba(239, 68, 68, 0.35)'),
                        borderRadius: '14px',
                        padding: '14px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.74rem', color: '#ffd873', fontWeight: 900, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            📲 Contacts de Confiance
                          </span>
                          <span style={{
                            fontSize: '0.66rem',
                            fontWeight: 800,
                            padding: '2px 8px',
                            borderRadius: '6px',
                            background: feedback.contactsCount > 0 ? 'rgba(242, 184, 7, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                            color: feedback.contactsCount > 0 ? '#ffd873' : '#fca5a5',
                            border: '1px solid ' + (feedback.contactsCount > 0 ? 'rgba(242, 184, 7, 0.4)' : 'rgba(239, 68, 68, 0.4)'),
                          }}>
                            {feedback.contactsCount > 0 ? `${feedback.contactsCount} configuré(s)` : '0 contact'}
                          </span>
                        </div>

                        {feedback.contactsCount > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '0.80rem', color: '#e2e8f0', lineHeight: 1.4 }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                              <span style={{ color: '#ffd873' }}>•</span>
                              <span><strong>{feedback.contactsCount} SMS d'urgence</strong> envoyés avec lien de secours géolocalisé.</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                              <span style={{ color: '#4ade80' }}>•</span>
                              <span><strong>{feedback.contactsSeen} contact(s)</strong> ont déjà ouvert le lien de géolocalisation.</span>
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontSize: '0.78rem', color: '#fca5a5', lineHeight: 1.4 }}>
                            ⚠️ <strong>0 Contact de Confiance configuré :</strong> Aucun SMS d'urgence n'a pu être transmis. (Vous pouvez ajouter des contacts via la tuile de gauche).
                          </div>
                        )}
                      </div>

                      {/* Bloc 2 : Alerte Communauté Protecteurs */}
                      <div style={{
                        background: 'rgba(5, 11, 20, 0.8)',
                        border: '1.5px solid ' + (feedback.isCommunityActive ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.35)'),
                        borderRadius: '14px',
                        padding: '14px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '0.74rem', color: feedback.isCommunityActive ? '#86efac' : '#fca5a5', fontWeight: 900, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            🛡️ Réseau Protecteurs (Rayon 270m)
                          </span>
                          <span style={{
                            fontSize: '0.66rem',
                            fontWeight: 800,
                            padding: '2px 8px',
                            borderRadius: '6px',
                            background: feedback.isCommunityActive ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                            color: feedback.isCommunityActive ? '#86efac' : '#fca5a5',
                            border: '1px solid ' + (feedback.isCommunityActive ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'),
                          }}>
                            {feedback.isCommunityActive ? '🟢 Actif' : '🔒 Tuile non débloquée'}
                          </span>
                        </div>

                        {feedback.isCommunityActive ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', fontSize: '0.80rem', color: '#e2e8f0', lineHeight: 1.4 }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                              <span style={{ color: '#38bdf8' }}>•</span>
                              <span><strong>{feedback.guardiansArea} citoyens équipés</strong> présents dans le périmètre des 270m.</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                              <span style={{ color: '#ffd873' }}>•</span>
                              <span><strong>{feedback.guardiansReceived} protecteur(s)</strong> ont reçu le signal d'alerte sur leur mobile.</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}>
                              <span style={{ color: '#4ade80' }}>•</span>
                              <span><strong>{feedback.guardiansAccepted} protecteur(s)</strong> ont accepté l'intervention et sont en route immédiate.</span>
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontSize: '0.78rem', color: '#fca5a5', lineHeight: 1.4 }}>
                            ❌ <strong>Tuile "Alerter la Communauté" non débloquée :</strong> Aucun citoyen Protecteur à proximité n'a été prévenu.
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Bouton d'action selon succès ou échec */}
                  {feedback.success ? (
                    <button
                      type="button"
                      onClick={onContinueSimulation}
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: '#ffffff',
                        border: 'none',
                        padding: '13px 20px',
                        borderRadius: '12px',
                        fontWeight: 900,
                        fontSize: '0.92rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 18px rgba(16, 185, 129, 0.45)',
                        marginTop: '4px',
                      }}
                    >
                      <span>Reprendre la simulation & Voir les Protecteurs converger</span>
                      <ArrowRight size={18} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={onContinueSimulation}
                      style={{
                        background: 'rgba(239, 68, 68, 0.85)',
                        color: '#ffffff',
                        border: 'none',
                        padding: '13px 20px',
                        borderRadius: '12px',
                        fontWeight: 900,
                        fontSize: '0.90rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        marginTop: '4px',
                      }}
                    >
                      <span>Continuer la simulation (Smartphone confisqué)</span>
                      <ArrowRight size={18} />
                    </button>
                  )}
                </div>
            )}

            {/* ==================== CORPS EN 3 COLONNES SUR LA CARTE ==================== */}
            {!feedback && (
              <div className="duel-responsive-grid">
                {/* 1. COLONNE GAUCHE : STATUT ET EMPLACEMENT DU TÉLÉPHONE */}
                <div className="duel-user-col" style={{
                  background: 'rgba(15, 39, 74, 0.65)',
                  border: '1.5px solid rgba(56, 189, 248, 0.35)',
                  borderRadius: '16px',
                  padding: '14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)', paddingBottom: '8px' }}>
                    <span style={{ fontSize: '1.2rem' }}>👩</span>
                    <div>
                      <strong style={{ color: '#38bdf8', fontSize: '0.88rem', display: 'block' }}>
                        Vous
                      </strong>
                      <span style={{ fontSize: '0.70rem', color: '#cbd5e1' }}>
                        Emplacement : <strong style={{ color: '#ffd873' }}>
                          {phoneLoc === 'in_hand' ? '📱 En main' : phoneLoc === 'in_pocket' ? '👖 Dans votre poche' : phoneLoc === 'confiscated' ? '❌ Confisqué' : '👜 Dans votre sac'}
                        </strong>
                      </span>
                    </div>
                  </div>

                  {/* AVERTISSEMENTS ET CONSEILS SELON L'EMPLACEMENT */}
                  <div style={{
                    background: phoneLoc === 'confiscated' ? 'rgba(239, 68, 68, 0.15)' : isPhoneInBagOrPocket ? 'rgba(245, 158, 11, 0.12)' : 'rgba(16, 185, 129, 0.12)',
                    border: '1px solid ' + (phoneLoc === 'confiscated' ? 'rgba(239, 68, 68, 0.45)' : isPhoneInBagOrPocket ? 'rgba(245, 158, 11, 0.35)' : 'rgba(16, 185, 129, 0.35)'),
                    borderRadius: '10px',
                    padding: '10px 12px',
                    fontSize: '0.74rem',
                    lineHeight: 1.45,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                  }}>
                    <div style={{ fontWeight: 800, color: phoneLoc === 'confiscated' ? '#f87171' : isPhoneInBagOrPocket ? '#ffd873' : '#86efac' }}>
                      {phoneLoc === 'in_hand' ? '📱 Mobile en main' : phoneLoc === 'in_pocket' ? '👖 Mobile dans la poche' : phoneLoc === 'confiscated' ? '❌ Mobile confisqué' : '👜 Mobile dans le sac'}
                    </div>

                    {phoneLoc === 'in_bag' && (
                      <div style={{ color: '#cbd5e1' }}>
                        Le téléphone est dans votre sac, certaines actions sont impossibles sans le sortir du sac.
                      </div>
                    )}
                    {phoneLoc === 'in_pocket' && (
                      <div style={{ color: '#cbd5e1' }}>
                        Le téléphone est dans votre poche, certaines actions sont impossibles sans le sortir de la poche.
                      </div>
                    )}
                    {phoneLoc === 'in_hand' && (
                      <div style={{ color: '#cbd5e1' }}>
                        Le téléphone est dans votre main, vous pouvez utiliser l'application MonAlerte pour lancer l'alerte, mais attention, l'agresseur peut vous en empêcher. Il est plus sûr d'utiliser une action plus rapide ou plus discrète.
                      </div>
                    )}
                    {phoneLoc === 'confiscated' && (
                      <div style={{ color: '#fca5a5' }}>
                        ❌ Votre smartphone a été confisqué par l'agresseur. Vous ne pouvez plus utiliser le téléphone.
                      </div>
                    )}
                  </div>
                </div>

                {/* 2. COLONNE CENTRALE : ACTIONS À FAIRE & PRISE EN MAIN */}
                <div className="duel-actions-col" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* OPTION DE SORTIE DU TÉLÉPHONE (SI DANS LE SAC OU DANS LA POCHE ET NON CONFISQUÉ) */}
                  {phoneLoc !== 'in_hand' && phoneLoc !== 'confiscated' && (
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.12) 0%, rgba(14, 165, 233, 0.20) 100%)',
                      border: '1.5px solid rgba(56, 189, 248, 0.45)',
                      borderRadius: '14px',
                      padding: '12px 14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                    }}>
                      <div>
                        <strong style={{ color: '#ffffff', fontSize: '0.86rem', display: 'block' }}>
                          📱 Sortir le smartphone de votre {phoneLoc === 'in_bag' ? 'sac' : 'poche'}
                        </strong>
                        <span style={{ fontSize: '0.72rem', color: '#cbd5e1', lineHeight: 1.35, display: 'block', marginTop: '2px' }}>
                          Passe le smartphone en main pour débloquer l'application MonAlerte.
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={handleDrawPhoneClick}
                        style={{
                          background: 'linear-gradient(135deg, #38bdf8 0%, #0284c7 100%)',
                          color: '#ffffff',
                          border: 'none',
                          padding: '10px 14px',
                          borderRadius: '10px',
                          fontWeight: 900,
                          fontSize: '0.78rem',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          boxShadow: '0 4px 15px rgba(56, 189, 248, 0.35)',
                        }}
                      >
                        📱 Sortir le téléphone
                      </button>
                    </div>
                  )}

                  {drawPhoneFeedback && (
                    <div style={{
                      background: drawPhoneFeedback.success ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid ' + (drawPhoneFeedback.success ? '#10b981' : '#ef4444'),
                      borderRadius: '10px',
                      padding: '10px 12px',
                      fontSize: '0.76rem',
                      lineHeight: 1.4,
                      color: drawPhoneFeedback.success ? '#86efac' : '#fca5a5',
                    }}>
                      {drawPhoneFeedback.message}
                    </div>
                  )}

                  {/* LISTE DES ACTIONS DISPONIBLES */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '0.78rem', color: 'var(--mga-gold)', fontWeight: 800, textTransform: 'uppercase' }}>
                      ⚔️ Actions de Réaction SOS :
                    </label>

                    {availableActions.map(action => {
                      const Icon = action.icon;
                      return (
                        <div
                          key={action.id}
                          onClick={() => handleExecuteAction(action)}
                          style={{
                            background: 'rgba(15, 39, 74, 0.65)',
                            border: '1.2px solid rgba(255, 255, 255, 0.12)',
                            borderRadius: '12px',
                            padding: '10px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '10px',
                              background: action.color + '25',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                              <Icon size={18} color={action.color} />
                            </div>
                            <div>
                              <h4 style={{ margin: 0, fontSize: '0.86rem', color: '#ffffff', fontWeight: 800 }}>
                                {action.label}
                              </h4>
                              <span style={{ fontSize: '0.70rem', color: 'var(--mga-slate-400)' }}>
                                {action.desc}
                              </span>
                            </div>
                          </div>

                          <div style={{
                            background: 'rgba(16, 185, 129, 0.15)',
                            border: '1px solid rgba(16, 185, 129, 0.35)',
                            color: '#86efac',
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '0.68rem',
                            fontWeight: 800,
                            whiteSpace: 'nowrap',
                          }}>
                            ⚡ Instantané
                          </div>
                        </div>
                      );
                    })}

                    {/* BOUTON NE RIEN FAIRE */}
                    <button
                      type="button"
                      onClick={handleDoNothingClick}
                      style={{
                        background: 'rgba(255, 255, 255, 0.04)',
                        border: '1px dashed rgba(255, 255, 255, 0.2)',
                        borderRadius: '10px',
                        padding: '8px 14px',
                        color: '#cbd5e1',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        marginTop: '4px',
                      }}
                    >
                      <span>✋ Ne rien faire (Subir sans réagir)</span>
                    </button>
                  </div>
                </div>

                {/* 3. COLONNE DROITE : STATISTIQUES DE L'AGRESSEUR */}
                <div className="duel-attacker-col" style={{
                  background: 'rgba(239, 68, 68, 0.08)',
                  border: '1.5px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '16px',
                  padding: '14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid rgba(239, 68, 68, 0.2)', paddingBottom: '8px' }}>
                    <span style={{ fontSize: '1.2rem' }}>🦹</span>
                    <div>
                      <strong style={{ color: '#f87171', fontSize: '0.85rem', display: 'block' }}>
                        {attackerProfile?.alias || "L'Agresseur"}
                      </strong>
                      <span style={{ fontSize: '0.68rem', color: '#fca5a5' }}>
                        {attackerProfile?.category || "Agression de rue"}
                      </span>
                    </div>
                  </div>

                  {/* Détermination / PV Ennemi */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.70rem', color: '#cbd5e1', marginBottom: '2px' }}>
                      <span>🦹 Détermination :</span>
                      <strong style={{ color: '#f87171' }}>{attackerHp !== undefined ? attackerHp : 65} / {maxAttackerHp || 65} PV</strong>
                    </div>
                    <div style={{ height: '6px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.max(0, Math.min(100, ((attackerHp !== undefined ? attackerHp : 65) / (maxAttackerHp || 65)) * 100))}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, #ef4444 0%, #f59e0b 100%)',
                      }}></div>
                    </div>
                  </div>

                  {/* Modus Operandi */}
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.35)',
                    borderRadius: '8px',
                    padding: '8px 10px',
                    fontSize: '0.68rem',
                    color: '#fed7aa',
                    lineHeight: 1.3,
                  }}>
                    <strong style={{ color: '#ffd873', display: 'block', marginBottom: '2px' }}>🎯 Modus Operandi :</strong>
                    {attackerProfile?.modusOperandi || "Tentative d'arrachage brutal et intimidation"}
                  </div>

                  {/* ATTENTION ATTRIBUT ATTENTIF (uniquement pertinent une fois la Touche Raccourcis débloquée) */}
                  {attackerProfile?.immunity === 'app_sos' && unlockedFeatures.includes('shortcutHardware') ? (
                    <div style={{
                      background: 'rgba(239, 68, 68, 0.2)',
                      border: '1px solid rgba(239, 68, 68, 0.45)',
                      borderRadius: '10px',
                      padding: '10px 12px',
                      fontSize: '0.72rem',
                      lineHeight: 1.4,
                      color: '#fca5a5',
                    }}>
                      ⚠️ <strong>Attention, l'agresseur est attentif :</strong> si vous essayez de sortir votre smartphone de votre {phoneLoc === 'in_bag' ? 'sac' : 'poche'} ou d'envoyer une alerte depuis l'application MonAlerte, il vous le prendra.
                    </div>
                  ) : (
                    <div style={{
                      background: 'rgba(15, 23, 42, 0.5)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '10px',
                      padding: '10px 12px',
                      fontSize: '0.72rem',
                      color: '#cbd5e1',
                      lineHeight: 1.4,
                    }}>
                      👁️ <strong>Posture :</strong> Concentré sur son intimidation physique, peu attentif à vos manipulations discrètes. Lancer une alerte depuis l'application MonAlerte peut passer, mais pas le sortir de la poche.
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* ==================== CAS 2 : INTERVENTION PROTECTEUR (3 COLONNES) ==================== */}
        {duelPhase === 'protector_strike' && activeProtector && (
          <>
            <div className="duel-responsive-grid">
              {/* 1. COLONNE GAUCHE : LE PROTECTEUR CITOYEN */}
              <div className="duel-user-col" style={{
                background: 'rgba(16, 185, 129, 0.12)',
                border: '1.5px solid rgba(16, 185, 129, 0.4)',
                borderRadius: '16px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid rgba(16, 185, 129, 0.3)', paddingBottom: '8px' }}>
                  <span style={{ fontSize: '1.4rem' }}>🛡️</span>
                  <div>
                    <strong style={{ color: '#86efac', fontSize: '0.88rem', display: 'block' }}>
                      {activeProtector.guardianTitle || "Protecteur Citoyen"}
                    </strong>
                    <span style={{ fontSize: '0.70rem', color: '#a7f3d0' }}>
                      Intervention de secours au contact
                    </span>
                  </div>
                </div>

                <div style={{
                  background: 'rgba(16, 185, 129, 0.15)',
                  borderRadius: '10px',
                  padding: '10px',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  textAlign: 'center',
                }}>
                  <span style={{ fontSize: '0.68rem', color: '#86efac', fontWeight: 800, textTransform: 'uppercase', display: 'block' }}>
                    💥 Force de Frappe Charisme
                  </span>
                  <strong style={{ fontSize: '1.3rem', color: '#4ade80' }}>
                    -{activeProtector.charisma === 3 ? 35 : activeProtector.charisma === 2 ? 25 : 15} PV
                  </strong>
                </div>

                <div style={{ fontSize: '0.72rem', color: '#cbd5e1', lineHeight: 1.4 }}>
                  🤝 <strong>Diversion active :</strong> Le Protecteur s'interpose pour vous protéger et faire reculer l'agresseur.
                </div>
              </div>

              {/* 2. COLONNE CENTRALE : LES MESSAGES & ACTION DE SORTIE DU SMARTPHONE */}
              <div className="duel-actions-col" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Dialogue du protecteur */}
                <div style={{
                  background: 'rgba(16, 185, 129, 0.15)',
                  borderRadius: '12px',
                  padding: '12px 14px',
                  borderLeft: '4px solid #10b981',
                  fontSize: '0.84rem',
                  color: '#ffffff',
                  lineHeight: 1.4,
                }}>
                  <strong style={{ color: '#4ade80' }}>[{activeProtector.guardianTitle || 'Protecteur'}] :</strong> <em>"Lâche cette personne immédiatement ! Le quartier entier est en alerte et la communauté arrive !"</em>
                </div>

                {/* Réponse de l'agresseur */}
                <div style={{
                  background: attackerHp <= 0 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.15)',
                  borderRadius: '12px',
                  padding: '10px 14px',
                  borderLeft: `4px solid ${attackerHp <= 0 ? '#ef4444' : '#f59e0b'}`,
                  fontSize: '0.82rem',
                  color: '#fed7aa',
                  lineHeight: 1.4,
                }}>
                  <strong style={{ color: attackerHp <= 0 ? '#f87171' : '#ffd873' }}>
                    [{attackerProfile?.alias || "L'Agresseur"} ({attackerHp <= 0 ? 'En Panique' : 'Sous Pression'})] :
                  </strong>{' '}
                  <em>
                    "{attackerHp <= 0 ? "Merde, ils rappliquent tous ! C'est bon je me tire !" : "Te mêle pas de ça toi, recule !"}"
                  </em>
                </div>

                {/* Impact de charisme */}
                <div style={{
                  background: 'rgba(5, 11, 20, 0.7)',
                  borderRadius: '10px',
                  padding: '8px 12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  fontSize: '0.78rem',
                  color: attackerHp <= 0 ? '#4ade80' : '#ffd873',
                  fontWeight: 700,
                  textAlign: 'center',
                }}>
                  {attackerHp <= 0
                    ? "🏆 L'agresseur est complètement déstabilisé (0 PV) et prend la fuite !"
                    : `💥 L'agresseur a perdu -${activeProtector.charisma === 3 ? 35 : activeProtector.charisma === 2 ? 25 : 15} PV de Charisme (reste ${attackerHp} PV).`}
                </div>

                {/* OPTION DE SORTIE DU TÉLÉPHONE */}
                {phoneLoc === 'confiscated' ? (
                  <div style={{
                    background: 'rgba(239, 68, 68, 0.12)',
                    border: '1px solid rgba(239, 68, 68, 0.35)',
                    borderRadius: '12px',
                    padding: '10px 14px',
                    fontSize: '0.76rem',
                    color: '#fca5a5',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <span>❌ <strong>Smartphone confisqué :</strong> L'agresseur vous a pris votre téléphone, vous ne pouvez pas le sortir.</span>
                  </div>
                ) : phoneLoc !== 'in_hand' ? (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.12) 0%, rgba(14, 165, 233, 0.20) 100%)',
                    border: '1.5px solid rgba(56, 189, 248, 0.45)',
                    borderRadius: '14px',
                    padding: '12px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                  }}>
                    <div>
                      <strong style={{ color: '#ffffff', fontSize: '0.86rem', display: 'block' }}>
                        📱 Sortir le smartphone de votre {phoneLoc === 'in_bag' ? 'sac' : 'poche'}
                      </strong>
                      <span style={{ fontSize: '0.72rem', color: '#cbd5e1', lineHeight: 1.35, display: 'block', marginTop: '2px' }}>
                        Passe le smartphone en main pour activer l'application MonAlerte et les preuves vidéo.
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        // Si le charisme de l'agresseur est > 45 : il n'est pas assez impressionné et reste vigilant, sortir le téléphone le fait saisir !
                        if (attackerHp > 45) {
                          setPhoneLoc('confiscated');
                          if (onDrawPhone) onDrawPhone('confiscated');
                          setDrawPhoneFeedback({
                            success: false,
                            message: "❌ TÉLÉPHONE SAISI : L'agresseur avait encore plus de 45 PV de Charisme et n'était pas assez impressionné. Il a vu votre geste et a saisi votre téléphone !",
                          });
                        } else {
                          // Réussite !
                          setPhoneLoc('in_hand');
                          if (onDrawPhone) onDrawPhone('in_hand');
                          setDrawPhoneFeedback({
                            success: true,
                            message: "📱 SUCCÈS : L'agresseur est sous pression et concentré sur le Protecteur ! Vous avez sorti votre smartphone en main (Capture vidéo active).",
                          });
                        }
                      }}
                      style={{
                        background: attackerHp > 45 ? 'rgba(239, 68, 68, 0.85)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: '#ffffff',
                        border: 'none',
                        padding: '10px 14px',
                        borderRadius: '10px',
                        fontWeight: 900,
                        fontSize: '0.78rem',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
                      }}
                    >
                      📱 Tenter de sortir le téléphone
                    </button>
                  </div>
                ) : (
                  <div style={{
                    background: 'rgba(16, 185, 129, 0.12)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    borderRadius: '12px',
                    padding: '10px 14px',
                    fontSize: '0.78rem',
                    color: '#86efac',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <CheckCircle size={16} color="#10b981" />
                    <span><strong>Smartphone en main :</strong> Objectif vidéo dégagé, identification du suspect en cours !</span>
                  </div>
                )}

                {drawPhoneFeedback && (
                  <div style={{
                    background: drawPhoneFeedback.success ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid ' + (drawPhoneFeedback.success ? '#10b981' : '#ef4444'),
                    borderRadius: '10px',
                    padding: '10px 12px',
                    fontSize: '0.76rem',
                    lineHeight: 1.4,
                    color: drawPhoneFeedback.success ? '#86efac' : '#fca5a5',
                  }}>
                    {drawPhoneFeedback.message}
                  </div>
                )}
              </div>

              {/* 3. COLONNE DROITE : ÉTAT ET CHARISME DE L'AGRESSEUR */}
              <div className="duel-attacker-col" style={{
                background: 'rgba(239, 68, 68, 0.08)',
                border: '1.5px solid rgba(239, 68, 68, 0.4)',
                borderRadius: '16px',
                padding: '14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid rgba(239, 68, 68, 0.2)', paddingBottom: '8px' }}>
                  <span style={{ fontSize: '1.2rem' }}>🦹</span>
                  <div>
                    <strong style={{ color: '#f87171', fontSize: '0.85rem', display: 'block' }}>
                      {attackerProfile?.alias || "L'Agresseur"}
                    </strong>
                    <span style={{ fontSize: '0.68rem', color: '#fca5a5' }}>
                      {attackerProfile?.category || "Agression de rue"}
                    </span>
                  </div>
                </div>

                {/* Détermination / PV Ennemi */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.70rem', color: '#cbd5e1', marginBottom: '2px' }}>
                    <span>🦹 Charisme & PV :</span>
                    <strong style={{ color: attackerHp <= 0 ? '#4ade80' : '#f87171' }}>
                      {Math.max(0, attackerHp)} / {maxAttackerHp || 65} PV
                    </strong>
                  </div>
                  <div style={{ height: '8px', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.max(0, Math.min(100, (attackerHp / (maxAttackerHp || 65)) * 100))}%`,
                      height: '100%',
                      background: attackerHp <= 0 ? '#4ade80' : 'linear-gradient(90deg, #ef4444 0%, #f59e0b 100%)',
                      transition: 'width 0.4s ease',
                    }}></div>
                  </div>
                </div>

                {/* ÉTAT DE L'AGRESSEUR SELON LE CHARISME (> 45 ou <= 45) */}
                {attackerHp > 45 ? (
                  <div style={{
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.45)',
                    borderRadius: '10px',
                    padding: '10px 12px',
                    fontSize: '0.72rem',
                    lineHeight: 1.4,
                    color: '#fca5a5',
                  }}>
                    ⚠️ <strong>Vigilant (> 45 PV) :</strong> L'agresseur n'est pas assez impressionné et reste vigilant. Sortir le téléphone serait dangereux, il peut le saisir.
                  </div>
                ) : (
                  <div style={{
                    background: 'rgba(16, 185, 129, 0.15)',
                    border: '1px solid rgba(16, 185, 129, 0.35)',
                    borderRadius: '10px',
                    padding: '10px 12px',
                    fontSize: '0.72rem',
                    lineHeight: 1.4,
                    color: '#86efac',
                  }}>
                    🛡️ <strong>Déstabilisé (≤ 45 PV) :</strong> L'agresseur est concentré sur le(s) protecteur(s), sortir le téléphone est envisageable.
                  </div>
                )}
              </div>
            </div>

            {/* Bouton pour continuer ou passer au rapport */}
            <button
              type="button"
              onClick={() => {
                if (attackerHp <= 0 && onFinishIncident) {
                  onFinishIncident();
                } else if (attackerHp <= 0 && onContinueSimulation) {
                  onContinueSimulation();
                } else if (onContinueSimulation) {
                  onContinueSimulation();
                }
              }}
              style={{
                background: attackerHp <= 0 
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                  : 'linear-gradient(135deg, #38bdf8 0%, #0284c7 100%)',
                color: '#ffffff',
                border: 'none',
                padding: '13px 20px',
                borderRadius: '14px',
                fontWeight: 900,
                fontSize: '0.96rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: attackerHp <= 0 
                  ? '0 6px 20px rgba(16, 185, 129, 0.4)' 
                  : '0 6px 20px rgba(56, 189, 248, 0.4)',
                marginTop: '6px',
              }}
            >
              <span>
                {attackerHp <= 0 
                  ? "🏆 Agresseur KO : Afficher le Rapport d'Enquête & Résultats" 
                  : `Reprendre la simulation (${attackerHp} PV restants - Les autres Protecteurs arrivent !)`}
              </span>
              <ArrowRight size={18} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default TurnBasedDuelModal;
